/**
 * @module SearchChat
 * @description Conversational search page with persistent thread, bottom-docked input,
 * and client-side intent router. Messages stack in a conversational thread — each query
 * appends a user bubble + assistant response. Intent queries are answered instantly
 * from an embedded knowledge base; everything else falls through to Shopify SRA.
 * @requires @fc/component
 * @requires @fc/search-chat-intents
 * @requires @fc/search-chat-renderers
 * @requires @fc/search-chat-storefront
 * @customElement search-chat
 */
import { FCElement } from '@fc/component';
import { DialogCloseEvent, DialogOpenEvent } from '@fc/dialog';
import {
  createSearchChatIntentEngine,
  parseSearchChatKnowledgeBase,
} from '@fc/search-chat-intents';
import { createSearchChatRenderer } from '@fc/search-chat-renderers';
import {
  clearSearchChatRecentlyViewed,
  fetchSearchChatAssistantMessage,
  fetchSearchChatLoadMore,
  fetchSearchChatRecentlyViewedCards,
  hydrateSearchChatFromUrl,
  normalizeStandardSearchTypeValue,
  resolveSearchUrl,
} from '@fc/search-chat-storefront';

function escapeHtml(value) {
  if (!value) return '';

  const element = document.createElement('span');
  element.textContent = String(value);
  return element.innerHTML;
}

class SearchChatComponent extends FCElement {
  requiredRefs = ['searchInput', 'thread', 'chatForm'];

  #searchFetch = null;
  #loadMoreFetch = null;
  #intentEngine = null;
  #pendingSearchRequest = null;
  #renderer = null;
  #translations = {};
  #pendingTyping = null;
  #scrollFrame = null;
  #followThread = true;

  get sectionId() {
    return this.dataset.sectionId;
  }

  get state() {
    return this.dataset.state || 'idle';
  }

  set state(value) {
    this.dataset.state = value;
  }

  get #showIcon() {
    return !this.hasAttribute('data-hide-icon');
  }

  get #isDrawer() {
    return this.hasAttribute('data-drawer');
  }

  connectedCallback() {
    super.connectedCallback();
    const signal = this.lifecycleSignal;

    this.addManagedEventListener(this, 'click', this.#handleDelegatedClick, { signal });
    this.addManagedEventListener(this.refs.thread, 'scroll', () => {
      const thread = this.refs.thread;
      this.#followThread = thread.scrollHeight - thread.clientHeight - thread.scrollTop <= 48;
    }, { signal });

    // Direct form submit listener (safety net for @submit delegation)
    this.addManagedEventListener(
      this.refs.chatForm,
      'submit',
      (e) => {
        e.preventDefault();
        this.handleSearch(e);
      },
      { signal },
    );

    const knowledgeBase = parseSearchChatKnowledgeBase(this);
    const entities = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" };
    this.#translations = Object.fromEntries(Object.entries(knowledgeBase?.translations || {}).map(([key, value]) => [key, String(value ?? '').replace(/&(amp|lt|gt|quot|#39);/g, (_, entity) => entities[entity])]));
    this.#intentEngine = createSearchChatIntentEngine({
      knowledgeBase,
      translations: this.#translations,
      esc: escapeHtml,
    });
    this.#renderer = createSearchChatRenderer({
      esc: escapeHtml,
      showIcon: this.#showIcon,
      translations: this.#translations,
      bubbleClass: this.dataset.bubbleClass || '',
    });

    // Page search can hydrate idle content immediately; closed drawers defer
    // optional recently viewed fetches until the shopper opens the surface.
    if (this.state === 'idle' && !this.#isDrawer) {
      this.#loadRecentlyViewed();
    }

    // Hydrate SSR content if page loaded with ?q=
    this.#hydrateSSR();

    // Auto-focus search input when drawer dialog opens
    if (this.#isDrawer) {
      const overlayDialog = this.closest('overlay-dialog');
      if (overlayDialog) {
        this.addManagedEventListener(overlayDialog, DialogOpenEvent.eventName, this.#handleOverlayOpen, { signal });
        this.addManagedEventListener(overlayDialog, DialogCloseEvent.eventName, this.#handleOverlayClose, { signal });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortAllFetches();
  }

  // ── Abort controller pattern ──

  #newSearchAbort() {
    this.#searchFetch?.abort();
    this.#pendingTyping?.remove();
    this.#pendingTyping = null;
    const ac = new AbortController();
    this.#searchFetch = ac;
    return ac;
  }

  #newLoadMoreAbort() {
    this.#loadMoreFetch?.abort();
    const ac = new AbortController();
    this.#loadMoreFetch = ac;
    return ac;
  }

  #abortAllFetches() {
    this.#searchFetch?.abort();
    this.#loadMoreFetch?.abort();
    this.#searchFetch = null;
    this.#loadMoreFetch = null;
    this.#pendingTyping?.remove();
    this.#pendingTyping = null;
    if (this.#scrollFrame != null) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
  }

  #waitForReply(signal) {
    return new Promise((resolve) => {
      if (signal.aborted) return resolve(false);
      const cancel = () => {
        clearTimeout(timer);
        resolve(false);
      };
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', cancel);
        resolve(!signal.aborted);
      }, 350);
      signal.addEventListener('abort', cancel, { once: true });
    });
  }

  #consumeSearchRequest(fallbackDisplayTerm) {
    const fallbackLabel = String(fallbackDisplayTerm ?? '').trim();
    const override =
      this.#pendingSearchRequest && typeof this.#pendingSearchRequest === 'object' ? this.#pendingSearchRequest : null;
    this.#pendingSearchRequest = null;

    const query = typeof override?.query === 'string' ? override.query.trim() : '';
    const label = typeof override?.label === 'string' ? override.label.trim() : '';
    const typeValue = typeof override?.typeValue === 'string' ? override.typeValue.trim() : '';

    return {
      label: label || fallbackLabel || query,
      query: query || fallbackLabel,
      typeValue,
    };
  }

  // ══════════════════════════════════════════════════════════
  // PUBLIC METHODS (bound via @click/@submit in Liquid)
  // ══════════════════════════════════════════════════════════

  /**
   * Handle search form submission.
   * Appends user bubble + assistant response to thread.
   * Tries intent router first, falls back to SRA extract.
   */
  async handleSearch(event) {
    event?.preventDefault();
    this.querySelector('predictive-search')?.close?.();

    const { searchInput, thread } = this.refs;
    if (!(searchInput instanceof HTMLInputElement)) return;

    const searchRequest = this.#consumeSearchRequest(searchInput.value.trim());
    const displayTerm = searchRequest.label;
    const term = searchRequest.query;
    if (!term) return;
    const fallbackTypeValue = this.#getTypeFilter();
    const searchTypeValue = this.#resolveSearchType(term, searchRequest.typeValue);
    const shouldPersistTypeOverride = !!searchTypeValue && searchTypeValue !== fallbackTypeValue;

    // Drawer search: redirect to the dedicated Shopify search route.
    if (this.#isDrawer) {
      this.#abortAllFetches();
      const url = new URL(resolveSearchUrl(FitCheck?.routes), location.origin);
      url.searchParams.set('q', term);
      if (searchTypeValue) url.searchParams.set('type', searchTypeValue);
      window.location.href = url.toString();
      return;
    }

    const ac = this.#newSearchAbort();
    this.#followThread = true;

    // Page search: update URL with query params while preserving route context.
    if (!this.#isDrawer) {
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('q', term);
      if (shouldPersistTypeOverride) {
        pageUrl.searchParams.set('type', searchTypeValue);
      } else {
        pageUrl.searchParams.delete('type');
      }
      history.replaceState(null, '', pageUrl.toString());
    }

    // Hide welcome, set active state
    this.#hideWelcome();
    this.state = 'results';

    // Show clear button
    this.#showClearBtn();

    thread.appendChild(this.#renderer.createUserBubble(displayTerm || term));
    this.#scrollToBottom();

    // Clear input immediately (chat behavior)
    searchInput.value = '';

    // Show typing indicator
    const typing = this.#renderer.createTypingIndicator();
    this.#pendingTyping = typing;
    thread.appendChild(typing);
    this.#scrollToBottom();

    // Try exact collection match before intents so high-confidence browse matches
    // beat generic store-info answers.
    const localResponse = this.#intentEngine?.resolveQuery?.(term);
    if (localResponse) {
      if (!(await this.#waitForReply(ac.signal))) return;
      typing.remove();
      this.#pendingTyping = null;
      const msg = this.#buildLocalMessage(localResponse, term);
      if (msg) {
        thread.appendChild(msg);
        this.#scrollToBottom();
        const results = msg.querySelector('.search-chat__results');
        if (results) this.#renderer.animateCards(results, 0);
        searchInput.focus({ preventScroll: true });
        return;
      }
    }

    // Fall through to SRA extract
    try {
      let msg = await fetchSearchChatAssistantMessage({
        sectionId: this.sectionId,
        query: term,
        typeValue: searchTypeValue,
        routes: FitCheck?.routes,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;

      if (!msg) {
        msg = this.#renderer.createAssistantMessage({
          html: `<p>${escapeHtml(this.#translations.no_results || '').replaceAll('[terms]', () => escapeHtml(term))}</p>`,
          followUps: [],
          sources: [],
        });
      }

      typing.remove();
      this.#pendingTyping = null;
      thread.appendChild(msg);

      // Append "View all results" link for drawer context
      if (this.#isDrawer) {
        const viewAllUrl = new URL(resolveSearchUrl(FitCheck?.routes), location.origin);
        viewAllUrl.searchParams.set('q', term);
        if (searchTypeValue) viewAllUrl.searchParams.set('type', searchTypeValue);
        this.#renderer.appendViewAllLink(
          msg,
          viewAllUrl.toString(),
          FitCheck.translations.view_all_results || '',
        );
      }

      this.#scrollToBottom();

      // Animate cards in the new message
      const results = msg.querySelector('.search-chat__results');
      if (results) this.#renderer.animateCards(results, 0);
    } catch (err) {
      typing.remove();
      if (ac.signal.aborted || err?.name === 'AbortError') return;
      this.#pendingTyping = null;
      const errMsg = this.#renderer.createAssistantMessage({
        html: `<p>${escapeHtml(this.#translations.search_error || '')}</p>`,
        followUps: [],
        sources: [],
      });
      thread.appendChild(errMsg);
      this.#scrollToBottom();
    }

    searchInput.focus({ preventScroll: true });
  }

  /**
   * Handle suggestion pill click. Sets input value and triggers search.
   * If the term matches a known collection, shows it as a card in the chat.
   */
  async searchSuggestion(term, event) {
    const { searchInput } = this.refs;
    if (!(searchInput instanceof HTMLInputElement)) return;
    this.querySelector('predictive-search')?.close?.();

    const decoded = decodeURIComponent(String(term).replace(/\+/g, ' '));

    // Check if suggestion matches a known collection → show as card in chat
    const match = this.#intentEngine?.matchCollection?.(decoded);
    if (match) {
      const { thread, searchInput: input } = this.refs;
      if (!thread) return;
      const ac = this.#newSearchAbort();
      this.#followThread = true;
      this.#hideWelcome();
      this.state = 'results';
      this.#showClearBtn();
      thread.appendChild(this.#renderer.createUserBubble(decoded));
      const typing = this.#renderer.createTypingIndicator();
      this.#pendingTyping = typing;
      thread.appendChild(typing);
      this.#scrollToBottom();
      if (!(await this.#waitForReply(ac.signal))) return;
      typing.remove();
      this.#pendingTyping = null;
      const msg = this.#buildCollectionCardMessage(match, decoded);
      thread.appendChild(msg);
      this.#scrollToBottom();
      const results = msg.querySelector('.search-chat__results');
      if (results) this.#renderer.animateCards(results, 0);
      if (input instanceof HTMLInputElement) input.focus({ preventScroll: true });
      return;
    }

    searchInput.value = decoded;
    return this.handleSearch(event);
  }

  /**
   * Load the next page of results and append cards within the specific message.
   */
  async loadMore(event) {
    event?.preventDefault();

    const button = event?.target instanceof Element ? event.target.closest('[data-next-url]') : null;
    if (!button) return;

    const nextUrl = button.getAttribute('data-next-url');
    if (!nextUrl) return;

    button.disabled = true;
    button.textContent = button.dataset.loadingText || '...';

    const ac = this.#newLoadMoreAbort();
    try {
      const { cards: newCards, loadMore: newLoadMore } = await fetchSearchChatLoadMore({
        sectionId: this.sectionId,
        nextUrl,
        signal: ac.signal,
      });
      if (ac.signal.aborted || !this.refs.thread?.contains(button)) return;

      // Find results container within THIS assistant message
      const resultsContainer = button
        .closest('.search-chat__message--assistant')
        ?.querySelector('.search-chat__results');

      if (resultsContainer && newCards.length) {
        const existingCount = resultsContainer.querySelectorAll('.search-chat__card').length;

        newCards.forEach((card, i) => {
          card.style.setProperty('--stagger-index', String(existingCount + i));
          resultsContainer.appendChild(card);
        });

        this.#renderer.animateCards(resultsContainer, existingCount);
      }

      const currentLoadMore = button.closest('.search-chat__load-more');
      if (currentLoadMore) {
        if (newLoadMore) {
          currentLoadMore.replaceWith(newLoadMore);
        } else {
          currentLoadMore.remove();
        }
      }
    } catch {
      if (ac.signal.aborted) return;
      button.disabled = false;
      button.textContent = button.dataset.labelText || '';
    }
  }

  /**
   * Clear thread and return to idle/welcome state.
   */
  handleClear(event) {
    event?.preventDefault();
    this.#abortAllFetches();
    this.#pendingSearchRequest = null;
    this.#followThread = true;
    this.querySelector('predictive-search')?.close?.();

    const { thread, searchInput } = this.refs;

    // Remove all messages and skeletons from thread
    if (thread) {
      thread.querySelectorAll('.search-chat__message, .search-chat__skeleton').forEach((el) => el.remove());
    }

    // Show welcome, set idle state
    this.#showWelcome();
    this.state = 'idle';
    this.#hideClearBtn();

    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
      searchInput.focus();
    }

    if (!this.#isDrawer) {
      history.replaceState(null, '', resolveSearchUrl(FitCheck?.routes));
    }

    // Reload recently viewed
    this.#loadRecentlyViewed();
  }

  // ══════════════════════════════════════════════════════════
  // COORDINATION HELPERS
  // ══════════════════════════════════════════════════════════

  #getTypeFilter() {
    const typeInput = this.refs.chatForm?.querySelector('input[name="type"]');
    return normalizeStandardSearchTypeValue(typeInput?.value?.trim?.() || '');
  }

  #resolveSearchType(query, preferredTypeValue = '') {
    const fallbackTypeValue = this.#getTypeFilter();
    const allowedTypes = String(fallbackTypeValue || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const normalizedPreferredType = normalizeStandardSearchTypeValue(preferredTypeValue);
    if (normalizedPreferredType && (!allowedTypes.length || allowedTypes.includes(normalizedPreferredType))) {
      return normalizedPreferredType;
    }

    if (!this.#intentEngine?.isProductDiscoveryQuery?.(query)) {
      return fallbackTypeValue;
    }

    if (!allowedTypes.length || allowedTypes.includes('product')) {
      return 'product';
    }

    return fallbackTypeValue;
  }

  #buildIntentMessage(intentId) {
    const content = this.#intentEngine?.buildIntentContent?.(intentId);
    return content ? this.#renderer.createAssistantMessage(content) : null;
  }

  #buildCollectionCardMessage(collection, term) {
    return this.#renderer.createCollectionCardMessage(collection, term);
  }

  #buildLocalMessage(response, term) {
    return response.kind === 'collection'
      ? this.#buildCollectionCardMessage(response.collection, term)
      : this.#buildIntentMessage(response.intentId);
  }

  // ══════════════════════════════════════════════════════════
  // THREAD MANAGEMENT
  // ══════════════════════════════════════════════════════════

  /**
   * Follow new exchanges inside the bounded thread without moving the page.
   * A shopper scrolling earlier messages pauses following until the next submit.
   */
  #scrollToBottom() {
    const { thread } = this.refs;
    if (!thread || !this.#followThread) return;
    if (this.#scrollFrame != null) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      if (!this.isConnected || !this.#followThread || !thread.clientHeight) return;
      if (['auto', 'scroll'].includes(getComputedStyle(thread).overflowY)) {
        thread.scrollTop = thread.scrollHeight;
      }
    });
  }

  /**
   * Hide the welcome/empty-state container and below-input area.
   */
  #hideWelcome() {
    const welcome = this.querySelector('.search-chat__welcome');
    if (welcome) welcome.hidden = true;
    const belowInput = this.querySelector('.search-chat__below-input');
    if (belowInput) belowInput.hidden = true;
  }

  /**
   * Show the welcome/empty-state container and below-input area.
   */
  #showWelcome() {
    const welcome = this.querySelector('.search-chat__welcome');
    if (welcome) welcome.hidden = false;
    const belowInput = this.querySelector('.search-chat__below-input');
    if (belowInput) belowInput.hidden = false;
  }

  /**
   * Show the clear/reset button.
   */
  #showClearBtn() {
    const btn = this.querySelector('.search-chat__clear-btn');
    if (btn) btn.hidden = false;
  }

  /**
   * Hide the clear/reset button.
   */
  #hideClearBtn() {
    const btn = this.querySelector('.search-chat__clear-btn');
    if (btn) btn.hidden = true;
  }

  /**
   * Hydrate SSR content on page load with ?q=.
   * If SSR rendered search results exist, keep them.
   * Use the same collection-first local response as a typed query.
   * Then clear the URL to the search route (session-based).
   */
  #hydrateSSR() {
    hydrateSearchChatFromUrl({
      searchUrl: FitCheck?.routes?.search_url,
      isDrawer: this.#isDrawer,
      thread: this.refs.thread,
      searchInput: this.refs.searchInput,
      resolveQuery: (query) => this.#intentEngine?.resolveQuery?.(query),
      buildLocalMessage: (response, query) => this.#buildLocalMessage(response, query),
      onResultsHydrated: () => {
        this.state = 'results';
        this.#hideWelcome();
        this.#showClearBtn();
      },
    });
  }

  // ══════════════════════════════════════════════════════════
  // DELEGATED CLICK HANDLER (follow-up chips)
  // ══════════════════════════════════════════════════════════

  #handleDelegatedClick = (e) => {
    const btn =
      e.target instanceof Element ? e.target.closest('[data-follow-up], [data-follow-up-query]') : null;
    if (!btn) return;

    const query = btn.getAttribute('data-follow-up')?.trim();
    const followUpQuery = btn.getAttribute('data-follow-up-query')?.trim() || query;
    const followUpType = btn.getAttribute('data-follow-up-type')?.trim() || '';
    const label = btn.textContent.trim();
    if (!followUpQuery) return;

    const { searchInput } = this.refs;
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = label || followUpQuery;
    }
    this.#pendingSearchRequest = {
      label: label || followUpQuery,
      query: followUpQuery,
      typeValue: followUpType,
    };

    // Route through handleSearch to append a new exchange
    this.handleSearch(new Event('submit', { cancelable: true }));
  };

  #handleOverlayOpen = () => {
    this.refs.searchInput?.focus();
    if (this.state === 'idle') {
      this.#loadRecentlyViewed();
    }
  };

  #handleOverlayClose = () => {
    this.#abortAllFetches();
    this.#pendingSearchRequest = null;
  };

  /**
   * Load recently viewed products into the empty state container.
   */
  async #loadRecentlyViewed() {
    const container = this.querySelector('.search-chat__recently-viewed');
    if (!(container instanceof HTMLElement)) return;

    try {
      const cards = await fetchSearchChatRecentlyViewedCards({
        sectionId: this.sectionId,
        routes: FitCheck?.routes,
        signal: this.lifecycleSignal,
      });
      if (this.lifecycleSignal.aborted || !this.isConnected || this.state !== 'idle' || !cards.length) return;

      this.#renderer.renderRecentlyViewed(container, cards, {
        signal: this.lifecycleSignal,
        onClear: () => clearSearchChatRecentlyViewed(),
      });
    } catch {
      /* silently fail */
    }
  }
}

if (!customElements.get('search-chat')) {
  customElements.define('search-chat', SearchChatComponent);
}
